import { accessGuard, getServerURL } from "../shared/sharedFunctions";
import companyService from "./companyService";

const UserService = () => {
	const pub = {};
	
	const ACCESS_TOKEN_KEY = "cympAccessToken";
	
	let user = null;
	pub.User = () => { return user; };
	
	let companyUsers = null;
	pub.CompanyUsers = () => { return companyUsers;	};
	
	pub.AccessLevel = () => { return user? user.access_level : -1; };
	
	let accessToken = null;
	pub.AccessToken = () => {
		if (!accessToken) {
			accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
		}
		return accessToken;
	};
	
	let lastUsedEmail = null;
	
	pub.login = async (email, password, code, remember) => {
		try {
			if (!email) {
				if (!lastUsedEmail) {
					throw new Error("no email provided");
				}
				email = lastUsedEmail;
			}
			lastUsedEmail = email;
			
			let body = {
				email: email
			};
			if (code && code.trim() !== "") {
				body.code = code;
			}
			else {
				body.password = password;
			}
			
			const req = {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(body)
			};
			
			const response = await fetch(`${getServerURL()}/api/users/login`, req);
			if (response.status !== 200) {
				throw new Error(`Login response ${response.status} ${response.statusText}`);
			}
			const data = await response.json();
			
			if (data.hasOwnProperty("email")) {
				if ("debug" in data) {
					console.log(`Debug Code: ${data.debug}`);
				}
				return 1;
			}
			else {
				accessToken = data.accessToken;
				if (remember) {
					localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
				}
				
				await processLoginData(data);
				
				return 2;
			}
		}
		catch (e) {
			console.log(e.message);
			throw e;
		}
	};
	
	pub.logout = () => {
		let deleteAccessToken = accessToken;
		
		localStorage.removeItem(ACCESS_TOKEN_KEY);
		accessToken = null;
		user = null;
		
		const req = {
			method: "GET",
			headers: { "Content-Type": "application/json", "access_token": deleteAccessToken }
		};
		fetch(`${getServerURL()}/api/users/logout`, req);
	};
	
	pub.authenticateAccessToken = async () => {
		let token = pub.AccessToken();
		if (!token) return false;
		
		try {
			const data = await validateAccessToken(token);
			if (!data) {
				throw new Error(`invalid response from auth`);
			}
			
			await processLoginData(data);
			
			return (pub.AccessToken() && pub.User());
		}
		catch (e) {
			console.log(`Error authenticating: ${e.message}`);
		}
		
		return false;
	};
	
	const processLoginData = async (data) => {
		user = data.user;
		companyService.company = data.company;
		if (pub.AccessLevel() === 1) {
			await companyService.all();
			const companies = companyService.companies;
			if (companies.length > 1) {
				await companyService.selectCompany(companies[0]);
			}
			else {
				await companyService.selectCompany(data.company);
			}
		}
		else {
			await companyService.selectCompany(data.company);
		}
	}
	
	pub.updateCompanyUsers = (company) => {
		return new Promise((resolve, reject) => {
			if (!accessGuard([1, 2, 3])) reject(new Error("Unauthorised"));
		
			const req = {
				method: "GET",
				headers: { "Content-Type": "application/json", "access_token": pub.AccessToken() },
			};
			
			fetch(`${getServerURL()}/api/users/all/${company.id}`, req)
			.then((response) => {
				if (response.status === 200) {
					return response.json();
				}
				else {
					throw new Error(response.status);
				}
			})
			.then((data) => {
				if (data === undefined || !data || !data.hasOwnProperty("users")) {
					throw new Error("invalid response from getting company users");
				}
				
				companyUsers = data.users;
				return resolve();
			})
			.catch(err => {
				console.log(err.message);
				reject(err);
			});
		});
		
	};
	
	pub.add = (user) => {
		return new Promise((resolve, reject) => {
			if (!accessGuard([1, 2])) return reject(new Error("Unauthorised"));
			
			if (!companyService.selectedCompany) {
				return reject(new Error("No selected company"));
			}
			
			const req = {
				method: "POST",
				headers: { "Content-Type": "application/json", "access_token": pub.AccessToken() },
				body: JSON.stringify({
					firstname: user.firstname,
					lastname: user.lastname,
					email: user.email,
					access_level: user.access_level,
					company_id: companyService.selectedCompany.id
				})
			};
			
			fetch(`${getServerURL()}/api/users/add`, req)
			.then((response) => {
				if (response.status === 200) {
					return response.json();
				}
				else {
					throw new Error(response.status);
				}
			})
			.then((data) => {
				if (data === undefined || !data || !data.hasOwnProperty("user")) {
					throw new Error("invalid response from adding user");
				}
				user = data.user;
				
				return pub.updateCompanyUsers(companyService.selectedCompany);
			})
			.then(() => {
				return resolve(user);
			})
			.catch(err => {
				console.log(err.message);
				reject(err);
			});
		});
	}
	
	pub.update = (user) => {
		return new Promise((resolve, reject) => {
			if (!accessGuard([1, 2])) reject(new Error("Unauthorised"));
			
			const req = {
				method: "PUT",
				headers: { "Content-Type": "application/json", "access_token": pub.AccessToken() },
				body: JSON.stringify({
					firstname: user.firstname,
					lastname: user.lastname,
					email: user.email,
					access_level: user.access_level,
					alerts: user.alerts
				})
			};
			
			fetch(`${getServerURL()}/api/users/update/${user.id}`, req)
			.then((response) => {
				if (response.status === 200) {
					return response.json();
				}
				else {
					throw new Error(response.status);
				}
			})
			.then((data) => {
				if (data === undefined || !data || !data.hasOwnProperty("user")) {
					throw new Error("invalid response from updating user");
				}
				user = data.user;
				
				return pub.updateCompanyUsers(companyService.selectedCompany);
			})
			.then(() => {
				return resolve(user);
			})
			.catch(err => {
				console.log(err.message);
				reject(err);
			});
		});
	};
	
	pub.delete = (user) => {
		return new Promise((resolve, reject) => {
			if (!accessGuard([1, 2])) reject(new Error("Unauthorised"));
			
			const req = {
				method: "DELETE",
				headers: { "Content-Type": "application/json", "access_token": pub.AccessToken() }
			};
			
			fetch(`${getServerURL()}/api/users/delete/${user.id}`, req)
			.then((response) => {
				if (response.status === 200) {
					return response.json();
				}
				else {
					throw new Error(response.status);
				}
			})
			.then(() => {
				return pub.updateCompanyUsers(companyService.selectedCompany);
			})
			.then(() => {
				return resolve(user);
			})
			.catch(err => {
				console.log(err.message);
				reject(err);
			});
		});
	}
	
	pub.forgotPassword = (email) => {
		return new Promise((resolve, reject) => {
			const req = {
				method: "POST",
				headers: { "Content-Type": "application/json", "access_token": pub.AccessToken() },
				body: JSON.stringify({
					email: email
				})
			};
			
			fetch(`${getServerURL()}/api/users/forgot`, req)
			.then((response) => {
				if (response.status === 200) {
					return response.json();
				}
				else {
					throw new Error(response.status);
				}
			})
			.then(() => {
				return resolve();
			})
			.catch(err => {
				console.log(err.message);
				reject(err);
			});
		});
	}
	
	pub.resetPassword = (token, password, confirmPassword) => {
		return new Promise((resolve, reject) => {
			const req = {
				method: "POST",
				headers: { "Content-Type": "application/json", "access_token": pub.AccessToken() },
				body: JSON.stringify({
					token: token,
					password: password,
					confirmPassword: confirmPassword
				})
			};
			
			fetch(`${getServerURL()}/api/users/resetpassword`, req)
			.then((response) => {
				if (response.status === 200) {
					return response.json();
				}
				else {
					throw new Error(response.status);
				}
			})
			.then(() => {
				return resolve();
			})
			.catch(err => {
				console.log(err.message);
				reject(err);
			});
		});
	}
	
	pub.changePassword = (existingPassword, password, confirmPassword) => {
		return new Promise((resolve, reject) => {
			const req = {
				method: "POST",
				headers: { "Content-Type": "application/json", "access_token": pub.AccessToken() },
				body: JSON.stringify({
					existingPassword: existingPassword,
					password: password,
					confirmPassword: confirmPassword
				})
			};
			
			fetch(`${getServerURL()}/api/users/changepassword`, req)
			.then((response) => {
				if (response.status === 200) {
					return response.json();
				}
				else {
					throw new Error(response.status);
				}
			})
			.then(() => {
				return resolve();
			})
			.catch(err => {
				console.log(err.message);
				reject(err);
			});
		});
	}
	
	pub.resendInvite = async (userId) => {
		const req = {
			method: "GET",
			headers: { "Content-Type": "application/json", "access_token": pub.AccessToken() }
		};
		
		const response = await fetch(`${getServerURL()}/api/users/resendinvite/${userId}`, req)
		if (response.status !== 200) {
			throw new Error(response.status);
		}
		
		const body = await response.json();
		return body.link;
	}
	
	const validateAccessToken = () => {
		return new Promise((resolve, reject) => {
			const req = {
				method: "GET",
				headers: { "Content-Type": "application/json", "access_token": accessToken }
			};
			
			fetch(`${getServerURL()}/api/users/auth`, req)
			.then((response) => {
				if (response.status === 401) {
					throw new Error("invalid credentials");
				}
				else if (response.status === 200) {
					return response.json();
				}
				else {
					throw new Error(response.status);
				}
			})
			.then((data) => {
				return resolve(data);
			})
			.catch((err) => {
				localStorage.removeItem(ACCESS_TOKEN_KEY);
				accessToken = null;
				user = null;
				reject(err);
			});
		});
	}
	
	return pub;
};

export default UserService();
